import { Component, Input, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { RestService } from 'app/shared/rest.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  FlightSeatMapResponse,
  FlightSeatMapCabin,
  FlightSeatMapRowType,
  FlightSeatMapRow,
  FlightSeatMapSeat,
  FlightSegment,
} from 'model/fly.interface';

interface SeatMapSegmentView {
  segmentNumber: number;
  label: string;
  cabins: FlightSeatMapCabin[];
}

@Component({
  selector: 'usb-seat-map',
  templateUrl: './seat-map.component.html',
  styleUrls: ['./seat-map.component.scss']
})
export class SeatMapComponent implements OnInit {

  // La oferta completa del vuelo (flightData del modal de detalle) — de acá
  // se sacan aerolínea/número de vuelo/equipo/horarios por segmento, ya que
  // la respuesta de seatmap sólo trae cabins/rows por segmentNumber, sin esos
  // datos descriptivos.
  @Input() flightData: any;
  @Input() providerId: string;
  @Input() bookingCode: string;

  loading = false;
  loadError = false;
  segments: SeatMapSegmentView[] = [];
  activeSegmentIndex = 0;

  constructor(
    public bsModalRef: BsModalRef,
    private restService: RestService,
  ) { }

  ngOnInit(): void {
    this.loadSeatMap();
  }

  closeModal(): void {
    this.bsModalRef.hide();
  }

  private loadSeatMap(): void {
    if (!this.bookingCode) {
      this.loadError = true;
      return;
    }

    this.loading = true;
    this.loadError = false;

    const payload = {
      products: [
        {
          categoriesId: [5],
          providerId: this.providerId,
          flights: { bookingCode: this.bookingCode },
        },
      ],
    };

    this.restService.doPost('app', 'flights/seatmap', payload).subscribe({
      next: (response: FlightSeatMapResponse) => {
        const result = response?.results?.[0];

        if (!result?.success) {
          this.loadError = true;
          this.loading = false;
          return;
        }

        this.segments = (result.segments || []).map(segment => ({
          segmentNumber: segment.segmentNumber,
          label: this.buildSegmentLabel(segment.segmentNumber),
          cabins: segment.cabins || [],
        }));

        this.loading = false;
        this.loadError = this.segments.length === 0;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error obteniendo el mapa de asientos:', error);
        this.loadError = true;
        this.loading = false;
      }
    });
  }

  // ==========================
  // Encabezado
  // ==========================

  private getOfferSegments(): FlightSegment[] {
    return this.flightData?.itinerary?.[0]?.options?.[0]?.segments || [];
  }

  private findOfferSegment(segmentNumber: number): FlightSegment {
    const segments = this.getOfferSegments();
    return segments.find(s => s.segmentNumber === segmentNumber) || segments[0];
  }

  private buildSegmentLabel(segmentNumber: number): string {
    const segment = this.findOfferSegment(segmentNumber);
    if (!segment) {
      return `Tramo ${segmentNumber}`;
    }
    return `${segment.airline?.code || ''}${segment.flight?.number || ''} - ${segment.flight?.equipment || ''}`;
  }

  get activeSegment(): SeatMapSegmentView {
    return this.segments[this.activeSegmentIndex];
  }

  get activeOfferSegment(): FlightSegment {
    return this.activeSegment ? this.findOfferSegment(this.activeSegment.segmentNumber) : null;
  }

  get routeOrigin(): string {
    return this.getOfferSegments()[0]?.departure?.airport || '';
  }

  get routeDestination(): string {
    const segments = this.getOfferSegments();
    return segments[segments.length - 1]?.arrival?.airport || '';
  }

  get routeDateLabel(): string {
    return this.formatDate(this.getOfferSegments()[0]?.departure?.date);
  }

  // itinerary[].duration viene a nivel de TRAMO (sibling de options, no
  // adentro de cada option) en la respuesta de /search — options[0].duration
  // queda de fallback por si algún proveedor lo devuelve ahí en cambio
  // (forma que usa /syncProducts).
  get routeDurationLabel(): string {
    return this.formatDuration(
      this.flightData?.itinerary?.[0]?.duration ?? this.flightData?.itinerary?.[0]?.options?.[0]?.duration
    );
  }

  selectSegment(index: number): void {
    this.activeSegmentIndex = index;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatDuration(minutes: number): string {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }

  // ==========================
  // Armado de la grilla de asientos
  // ==========================

  getRowsForRange(cabin: FlightSeatMapCabin, rowType: FlightSeatMapRowType): FlightSeatMapRow[] {
    return (cabin.rows || [])
      .filter(row => row.rowNumber >= rowType.fromRow && row.rowNumber <= rowType.toRow)
      .sort((a, b) => a.rowNumber - b.rowNumber);
  }

  // El ejemplo de respuesta usado para tipar esto no trae un nombre de
  // cabina explícito — se intentan algunos campos plausibles y, si no
  // aparece ninguno, se cae a un título genérico.
  getCabinTitle(cabin: FlightSeatMapCabin, index: number): string {
    return cabin.cabinClass || cabin.name || cabin.className || `Cabina ${index + 1}`;
  }

  // Agrupa columnas consecutivas separando por el pasillo: cuando dos
  // columnas seguidas están AMBAS tipadas como pasillo ("A" / AISLE), ahí va
  // el corte. Funciona tanto para layouts de un pasillo (3-3) como de dos
  // (3-3-3) y para asientos todos-pasillo (business tipo herringbone, donde
  // cada butaca queda aislada).
  getColumnGroups(rowType: FlightSeatMapRowType): string[][] {
    const seatTypes = rowType?.seatTypes || [];
    const groups: string[][] = [];
    let current: string[] = [];

    seatTypes.forEach((seatType, index) => {
      current.push(seatType.column);
      const next = seatTypes[index + 1];
      const isAisleBoundary = seatType.type === 'A' && next?.type === 'A';

      if (isAisleBoundary || !next) {
        groups.push(current);
        current = [];
      }
    });

    return groups;
  }

  getSeatForColumn(row: FlightSeatMapRow, column: string): FlightSeatMapSeat {
    return row?.seats?.find(seat => seat.column === column) || null;
  }

  // ==========================
  // Estado visual del asiento
  // ==========================

  getSeatStatusClass(seat: FlightSeatMapSeat): string {
    if (!seat) {
      return 'seat-empty';
    }
    if (!seat.available || seat.status === 'O') {
      return 'seat-unavailable';
    }

    const labels = [...(seat.typeLabels || []), ...(seat.types || [])].map(l => (l || '').toUpperCase());

    if (labels.some(l => l.includes('PREFERRED'))) {
      return 'seat-preferred';
    }
    if (labels.some(l => l.includes('EXTRA'))) {
      return 'seat-extra';
    }
    if (labels.some(l => l.includes('REAR') || l.includes('BACK') || l.includes('AFT'))) {
      return 'seat-rear-facing';
    }
    return 'seat-available';
  }

  isRearFacing(seat: FlightSeatMapSeat): boolean {
    return this.getSeatStatusClass(seat) === 'seat-rear-facing';
  }

  isUnavailable(seat: FlightSeatMapSeat): boolean {
    return this.getSeatStatusClass(seat) === 'seat-unavailable';
  }
}
